/**
 * Generic polling utility for async operations.
 *
 * Repeatedly calls `checkFn` at a fixed interval until it signals completion,
 * failure, or the maximum number of attempts is reached.
 *
 * @param {Function} checkFn - Async function called each poll. Must return an object:
 *   - { done: true, result }  → polling stops, promise resolves with `result`
 *   - { done: false }         → keep polling
 *   - throw                   → treated as transient error; polling continues until maxPolls
 * @param {Object}   [options]
 * @param {number}   [options.interval=3000]       - Milliseconds between polls
 * @param {number}   [options.maxPolls=20]          - Maximum number of poll attempts
 * @param {Function} [options.onPoll]               - Optional callback(pollCount) after each poll
 * @param {string}   [options.timeoutMessage]       - Error message when maxPolls is exceeded
 * @returns {Promise<*>} Resolves with the value returned by `checkFn`
 *
 * @example
 * // Image upload — poll until analysis record exists
 * const result = await pollForResult(
 *   async () => {
 *     const exists = await ImageAnalysisAPI.analysisExists(uploadId);
 *     return { done: exists };
 *   },
 *   { interval: 3000, maxPolls: 20 },
 * );
 *
 * @example
 * // Social media — poll until status is COMPLETED and return the result
 * const analysis = await pollForResult(
 *   async () => {
 *     const resp = await SocialMediaAnalysisAPI.checkAnalysisStatus(id);
 *     if (resp.status === "COMPLETED") return { done: true, result: resp.result };
 *     if (resp.status === "FAILED")    throw new Error(resp.message || "Analysis failed");
 *     return { done: false };
 *   },
 *   { interval: 3000, maxPolls: 30, onPoll: (n) => console.log("poll", n) },
 * );
 */
export default function pollForResult(checkFn, options = {}) {
  const {
    interval = 3000,
    maxPolls = 20,
    onPoll = null,
    timeoutMessage = "Operation timed out. Please try again or refresh in a moment.",
  } = options;

  return new Promise((resolve, reject) => {
    let pollCount = 0;

    const timer = setInterval(async () => {
      pollCount++;

      try {
        const outcome = await checkFn(pollCount);

        if (onPoll) {
          onPoll(pollCount);
        }

        if (outcome && outcome.done) {
          clearInterval(timer);
          resolve(outcome.result);
          return;
        }

        // Still processing — check timeout
        if (pollCount >= maxPolls) {
          clearInterval(timer);
          reject(new Error(timeoutMessage));
        }
      } catch (error) {
        // Treat thrown errors as transient unless we've exhausted attempts
        console.warn(
          `pollForResult: error on attempt ${pollCount}/${maxPolls}:`,
          error.message,
        );

        if (pollCount >= maxPolls) {
          clearInterval(timer);
          // If checkFn threw intentionally (e.g. FAILED status), propagate that error
          reject(error);
        }
        // else keep polling
      }
    }, interval);
  });
}
